import { ImportCounts, ImportField } from 'app/core/services/contact-import/contact-import.types';
import { IMPORT_FIELD_OPTIONS, isRepeatableField } from './import-fields';

/** Counts as the app writes them: US grouping, "1,204". */
export function formatCount(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

/**
 * The fields column `index` may pick: every field but the single-column ones another
 * column already took. Its own current field always stays.
 */
export function mappingOptionsFor(
  mapping: ImportField[],
  index: number
): { value: ImportField; label: string }[] {
  const taken = new Set(
    mapping.filter(
      (field, column) => column !== index && field !== 'IGNORE' && !isRepeatableField(field)
    )
  );
  return IMPORT_FIELD_OPTIONS.filter((option) => !taken.has(option.value));
}

/** The review footer's sentence, as an i18n key and its numbers. */
export interface ImportFooter {
  key: string;
  params: Record<string, string>;
  /** "Actualizar reemplaza datos que ya existen." follows. */
  warnsReplace: boolean;
  /** Nothing would be written: Importar stays off. */
  empty: boolean;
}

/** "Vas a crear 1,204 contactos (1,203 también como clientes) y actualizar 2." */
export function importFooter(counts: ImportCounts): ImportFooter {
  const params = {
    create: formatCount(counts.toCreate),
    customers: formatCount(counts.toCreateAsCustomers),
    update: formatCount(counts.toUpdate),
  };
  const creates = counts.toCreate > 0;
  const updates = counts.toUpdate > 0;
  // "(0 también como clientes)" is noise: the sentence leaves the customers out then.
  const plain = counts.toCreateAsCustomers > 0 ? '' : '_PLAIN';
  const key =
    creates && updates
      ? `IMPORT_FOOTER_CREATE_UPDATE${plain}`
      : creates
        ? `IMPORT_FOOTER_CREATE${plain}`
        : updates
          ? 'IMPORT_FOOTER_UPDATE'
          : counts.toMakeCustomers > 0
            ? 'IMPORT_FOOTER_CUSTOMERS_ONLY'
            : 'IMPORT_FOOTER_NOTHING';
  return {
    key,
    params: { ...params, makeCustomers: formatCount(counts.toMakeCustomers) },
    warnsReplace: updates,
    empty: !creates && !updates && counts.toMakeCustomers === 0,
  };
}

/** An import notice read back: the file, and the sentence its counts make. */
export interface ImportNoticeDetail {
  file: string;
  labelKey: string;
  params: Record<string, string>;
}

/**
 * The bell's description, "{file} — {created}/{not imported}/{updated}" (older notices
 * lack the updated count), read back; null for anything else. The updated contacts are
 * named only when there are some.
 */
export function importNoticeDetail(
  description: string | null | undefined
): ImportNoticeDetail | null {
  const match = /^(.*) — (\d+)\/(\d+)(?:\/(\d+))?$/.exec(description ?? '');
  if (!match) {
    return null;
  }
  const params: Record<string, string> = {
    created: formatCount(+match[2]),
    failed: formatCount(+match[3]),
  };
  const updated = +(match[4] ?? 0);
  if (updated === 0) {
    return { file: match[1], labelKey: 'NOTIF_IMPORT_DETAIL', params };
  }
  return {
    file: match[1],
    labelKey: 'NOTIF_IMPORT_DETAIL_UPDATED',
    params: { ...params, updated: formatCount(updated) },
  };
}
